/**
 * SidebarMenu Component
 * Claude-style sidebar navigation menu
 * Responsive: ~25% width on web, fullscreen on mobile
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  Pressable,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { useAuthStore } from '../../store/authStore';
import { useOnboardingStore } from '../../store/onboardingStore';
import { subscriptionsApi, SubscriptionStatus } from '../../api/endpoints/subscriptions';
import PaywallModal from '../common/PaywallModal';
import logger from '../../utils/logger';

export type SidebarRoute = 'Dashboard' | 'Chats' | 'Goals' | 'Budget' | 'Accounts' | 'Settings';

interface MenuItem {
  id: SidebarRoute;
  label: string;
  icon: string;
  iconActive?: string;
}

const menuItems: MenuItem[] = [
  { id: 'Dashboard', label: 'Dashboard', icon: 'view-dashboard-outline', iconActive: 'view-dashboard' },
  { id: 'Budget', label: 'Budget', icon: 'wallet-outline', iconActive: 'wallet' },
  { id: 'Accounts', label: 'Comptes', icon: 'bank-outline', iconActive: 'bank' },
  { id: 'Goals', label: 'Objectifs', icon: 'target', iconActive: 'target' },
  { id: 'Chats', label: 'Chats', icon: 'chat-processing-outline', iconActive: 'chat-processing' },
];

interface SidebarMenuProps {
  activeRoute: SidebarRoute;
  onNavigate: (route: SidebarRoute) => void;
  onSettingsPress?: () => void;
  onSignOut?: () => void;
  isCollapsed?: boolean;
}

export default function SidebarMenu({
  activeRoute,
  onNavigate,
  onSettingsPress,
  onSignOut,
  isCollapsed = false,
}: SidebarMenuProps) {
  const { user, logout } = useAuthStore();
  const { currentStep } = useOnboardingStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);

  // Fetch subscription status on mount and when paywall closes
  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionsApi.getCurrentSubscription();
      setSubscription(response.subscription);
    } catch (error) {
      logger.error('Error fetching subscription:', error);
    }
  };

  const isPremium = subscription?.is_premium || false;

  const getPlanLabel = () => {
    if (!subscription) return 'Free plan';
    if (subscription.is_premium) {
      return subscription.plan === 'pro' ? 'Pro' : 'Premium';
    }
    return 'Free plan';
  };

  const handleSubscribeSuccess = () => {
    setShowPaywall(false);
    fetchSubscription(); // Refresh subscription status
  };

  // Get first_name/last_name from authStore or onboardingStore profile
  const firstName = user?.first_name || currentStep?.profile?.first_name;
  const lastName = user?.last_name || currentStep?.profile?.last_name;
  const email = user?.email;

  const getUserInitials = () => {
    // Priority: First+Last initials > First initial > Email initial > fallback
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'JA';
  };

  const getUserDisplayName = () => {
    // Priority: Firstname Lastname > Email > fallback
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }
    if (email) {
      return email;
    }
    return 'Utilisateur';
  };

  const handleSignOut = async () => {
    setShowUserMenu(false);
    if (onSignOut) {
      onSignOut();
    } else {
      await logout();
    }
  };

  return (
    <View style={styles.container}>
      {/* Logo Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoText}>J</Text>
          </View>
          {!isCollapsed && (
            <Text style={styles.logoTitle}>Wealfi</Text>
          )}
        </View>
      </View>

      {/* Navigation Items */}
      <ScrollView
        style={styles.menuContainer}
        showsVerticalScrollIndicator={false}
      >
        {menuItems.map((item) => {
          const isActive = activeRoute === item.id;
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.menuItem,
                isActive && styles.menuItemActive,
              ]}
              onPress={() => onNavigate(item.id)}
              activeOpacity={0.7}
            >
              <Icon
                name={isActive ? (item.iconActive || item.icon) : item.icon}
                size={20}
                color={isActive ? colors.sidebar.accent : colors.sidebar.text}
              />
              {!isCollapsed && (
                <Text
                  style={[
                    styles.menuItemText,
                    isActive && styles.menuItemTextActive,
                  ]}
                >
                  {item.label}
                </Text>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* User Profile Section */}
      <View style={styles.userSection}>
        <TouchableOpacity
          style={styles.userButton}
          onPress={() => setShowUserMenu(!showUserMenu)}
          activeOpacity={0.8}
        >
          <View style={styles.userAvatar}>
            <Text style={styles.userInitials}>{getUserInitials()}</Text>
          </View>
          {!isCollapsed && (
            <>
              <View style={styles.userInfo}>
                <Text style={styles.userName} numberOfLines={1}>
                  {getUserDisplayName()}
                </Text>
                <TouchableOpacity
                  onPress={(e) => {
                    e.stopPropagation();
                    setShowUserMenu(false);
                    if (!isPremium) {
                      setShowPaywall(true);
                    }
                  }}
                  activeOpacity={isPremium ? 1 : 0.7}
                >
                  <Text style={[styles.userPlan, isPremium && styles.userPlanPremium]}>
                    {getPlanLabel()}
                  </Text>
                </TouchableOpacity>
              </View>
              <Icon
                name={showUserMenu ? 'chevron-down' : 'chevron-up'}
                size={16}
                color={colors.sidebar.textSecondary}
              />
            </>
          )}
        </TouchableOpacity>

        {/* User Dropdown Menu */}
        {showUserMenu && !isCollapsed && (
          <View style={styles.userDropdown}>
            <Text style={styles.userEmail} numberOfLines={1}>
              {user?.email}
            </Text>

            {isPremium ? (
              <>
                {/* Premium user: Show subscription info and cancel option */}
                <View style={styles.dropdownItemPremiumStatus}>
                  <Icon name="crown" size={18} color={colors.primary} />
                  <View style={styles.premiumStatusInfo}>
                    <Text style={styles.dropdownItemTextPremium}>Abonnement {getPlanLabel()}</Text>
                    {subscription?.current_period_end && (
                      <Text style={styles.subscriptionDate}>
                        Actif jusqu'au {new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}
                      </Text>
                    )}
                  </View>
                </View>

              </>
            ) : (
              <>
                {/* Free user: Show upgrade option */}
                <TouchableOpacity
                  style={styles.dropdownItemUpgrade}
                  onPress={() => {
                    setShowUserMenu(false);
                    setShowPaywall(true);
                  }}
                >
                  <Icon name="crown" size={18} color={colors.primary} />
                  <Text style={styles.dropdownItemTextUpgrade}>Passer à Premium</Text>
                </TouchableOpacity>
              </>
            )}

            <View style={styles.dropdownDivider} />

            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => {
                setShowUserMenu(false);
                onSettingsPress?.();
              }}
            >
              <Icon name="cog-outline" size={18} color={colors.sidebar.text} />
              <Text style={styles.dropdownItemText}>Paramètres</Text>
            </TouchableOpacity>

            <View style={styles.dropdownDivider} />

            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={handleSignOut}
            >
              <Icon name="logout" size={18} color={colors.sidebar.text} />
              <Text style={styles.dropdownItemText}>Se déconnecter</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Paywall Modal */}
      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        onSubscribeSuccess={handleSubscribeSuccess}
        trigger="feature"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.sidebar.background,
    borderRightWidth: 1,
    borderRightColor: colors.sidebar.border,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  logoIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
  },
  logoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.sidebar.text,
  },
  menuContainer: {
    flex: 1,
    paddingHorizontal: 8,
    paddingTop: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 2,
  },
  menuItemActive: {
    backgroundColor: colors.sidebar.backgroundActive,
  },
  menuItemText: {
    fontSize: 14,
    color: colors.sidebar.text,
    fontWeight: '500',
  },
  menuItemTextActive: {
    color: colors.sidebar.accent,
    fontWeight: '600',
  },
  userSection: {
    borderTopWidth: 1,
    borderTopColor: colors.sidebar.border,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  userButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.sidebar.userBadge,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userInitials: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.sidebar.text,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.sidebar.text,
  },
  userPlan: {
    fontSize: 12,
    color: colors.sidebar.textMuted,
  },
  userPlanPremium: {
    color: colors.primary,
    fontWeight: '600',
  },
  userDropdown: {
    backgroundColor: colors.background,
    borderRadius: 12,
    marginTop: 8,
    marginHorizontal: 4,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  userEmail: {
    fontSize: 13,
    color: colors.sidebar.textMuted,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  dropdownItemUpgrade: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: `${colors.primary}10`,
    borderRadius: 8,
    marginHorizontal: 8,
    marginTop: 4,
  },
  dropdownItemText: {
    fontSize: 14,
    color: colors.sidebar.text,
  },
  dropdownItemTextUpgrade: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '600',
  },
  dropdownItemPremiumStatus: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: `${colors.primary}10`,
    borderRadius: 8,
    marginHorizontal: 8,
    marginTop: 4,
  },
  premiumStatusInfo: {
    flex: 1,
  },
  dropdownItemTextPremium: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '600',
  },
  subscriptionDate: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: colors.sidebar.border,
    marginVertical: 4,
    marginHorizontal: 12,
  },
});
